import type { ReactNode } from 'react';
import { formatTaskKey,
  TASK_PRIORITY_LABEL,
  TASK_STATUS_LABEL,
  type TaskSummary,
} from '@todo/shared';
import { PRIORITY_BADGE, PRIORITY_DOT, STATUS_TONE, dueInfo, initials } from '../lib/task-ui';
import { StarButton } from './StarButton';

/**
 * The one task card every list page renders, styled like a My Day board
 * card - priority and due date up front, compact, laid out in a grid - but
 * static: there is no board to drag between here, a click just opens the
 * detail panel. Behaviour is opt-in through callbacks: a page that cannot
 * complete tasks simply omits onToggleDone and the circle disappears - the
 * card never guesses at permissions itself.
 */

export type TaskCardProps = {
  task: TaskSummary;
  busy?: boolean;
  /** Whole-card click (buttons excluded) - open the detail panel. */
  onOpen: (task: TaskSummary) => void;
  /** Circle check: DONE <-> reopen. Omit to hide the circle. */
  onToggleDone?: (task: TaskSummary) => void;
  /** Star. Omit to hide it. */
  onToggleImportant?: (task: TaskSummary) => void;
  /** Pages about a fixed person (e.g. Assigned to me) can drop the chip. */
  hideAssignee?: boolean;
  /** Page-specific extras rendered under the meta row (decline reasons, cancel...). */
  footer?: ReactNode;
  /** Lets the card be picked up and dropped on a board column to change its
   *  status - the same gesture My Day introduced. Omit (the default) for a
   *  page with no board underneath it to drop onto. */
  draggable?: boolean;
  /** This task's sub-tasks, rendered INSIDE the card as a checklist (the
   *  Azure Boards look): tick to complete, click a row to open it. The
   *  board supplies these only when the parent is on the board; a sub-task
   *  whose parent is not visible stays a card of its own. */
  subtasks?: TaskSummary[];
};

export function TaskCard({
  task, busy = false, onOpen, onToggleDone, onToggleImportant,
  hideAssignee = false, footer, draggable = false,
  subtasks,
}: TaskCardProps) {
  const tone = STATUS_TONE[task.status];
  const due = dueInfo(task);
  const done = task.status === 'DONE';
  const closed = done || task.status === 'CANCELLED';
  const first = task.assignees[0];
  const pct = task.subtaskCount > 0
    ? Math.round((task.subtaskDoneCount / task.subtaskCount) * 100)
    : 0;
  const openSubtasks = task.subtaskCount - task.subtaskDoneCount;
  const isSubtask = !!task.parentTaskId;

  return (
    <li
      draggable={draggable && !busy}
      onDragStart={draggable ? (e) => e.dataTransfer.setData('text/plain', task.id) : undefined}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) return;
        onOpen(task);
      }}
      className={[
        'flex h-full flex-col gap-1.5 rounded-lg border border-l-[3px] bg-white p-3 shadow-sm transition hover:border-slate-300 hover:shadow',
        draggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer',
        tone.edge,
        due.bucket === 'overdue' && !closed ? 'ring-1 ring-red-100' : '',
        closed ? 'opacity-70' : '',
      ].join(' ')}
    >
      {/* Priority + status + due, at a glance before anything else. */}
      <div className="flex items-center gap-1.5">
        {onToggleDone && (
          <button
            type="button"
            disabled={busy || task.status === 'CANCELLED'}
            title={
              done
                ? 'Reopen task'
                : openSubtasks > 0
                  ? `${openSubtasks} sub-task${openSubtasks === 1 ? '' : 's'} still open - finish them first`
                  : 'Mark complete'
            }
            onClick={() => onToggleDone(task)}
            className={[
              'grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full text-[10px] font-bold transition disabled:opacity-40',
              done
                ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                : 'border-[1.5px] border-slate-300 bg-white text-transparent hover:border-emerald-500 hover:text-emerald-500',
            ].join(' ')}
          >
            &#10003;
          </button>
        )}
        <span
          className={`h-2 w-2 shrink-0 rounded-full ${PRIORITY_DOT[task.priority]}`}
          title={TASK_PRIORITY_LABEL[task.priority]}
        />
        <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ring-1 ${tone.pill}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />
          {TASK_STATUS_LABEL[task.status]}
        </span>
        <span className={`ml-auto shrink-0 text-[11px] font-medium ${due.cls}`}>{due.text}</span>
      </div>

      {/* Key (+ sub-task marker), creator, department. */}
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] text-slate-400">
        {isSubtask && <span aria-hidden="true">&#8627;</span>}
        <span className="font-mono">{formatTaskKey(task.taskKey)}</span>
        {isSubtask && (
          <span className="rounded bg-slate-100 px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-slate-500">
            Sub
          </span>
        )}
        <span className="text-slate-300">&middot;</span>
        <span>by {task.createdByName}</span>
        {task.departmentName && (
          <>
            <span className="text-slate-300">&middot;</span>
            <span>{task.departmentName}</span>
          </>
        )}
        {task.projectName && (
          <span
            title={`Project: ${task.projectName}`}
            className="rounded bg-brand-50 px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-brand-700 ring-1 ring-brand-100"
          >
            {task.projectName}
          </span>
        )}
      </div>

      {/* Title, and priority spelled out - the dot up top is a hint, this is the label. */}
      <div className="flex items-start justify-between gap-2">
        <p className={[
          'line-clamp-2 text-sm font-medium leading-snug',
          done ? 'text-slate-400 line-through' : 'text-slate-900',
        ].join(' ')}>
          {task.title}
        </p>
        <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold ${PRIORITY_BADGE[task.priority]}`}>
          {TASK_PRIORITY_LABEL[task.priority]}
        </span>
      </div>

      {task.subtaskCount > 0 && (
        <div className="flex items-center gap-1.5">
          <div className="h-1 flex-1 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-emerald-400" style={{ width: `${pct}%` }} />
          </div>
          <span className="text-[10px] tabular-nums text-slate-400">
            {task.subtaskDoneCount}/{task.subtaskCount}
          </span>
        </div>
      )}

      {/* The sub-tasks themselves, checklist-style inside the parent card
          (Azure Boards look). Rows never drag - the PARENT is the card. */}
      {subtasks && subtasks.length > 0 && (
        <ul className="space-y-1 rounded-md bg-slate-50 p-1.5 ring-1 ring-slate-100">
          {subtasks.map((sub) => {
            const subDone = sub.status === 'DONE';
            const subClosed = subDone || sub.status === 'CANCELLED';
            const owner = sub.assignees[0];
            return (
              <li key={sub.id} className="flex items-center gap-1.5">
                {onToggleDone ? (
                  <button
                    type="button"
                    disabled={busy || sub.status === 'CANCELLED'}
                    title={subDone ? 'Reopen sub-task' : 'Mark sub-task complete'}
                    onClick={() => onToggleDone(sub)}
                    className={[
                      'grid h-[14px] w-[14px] shrink-0 place-items-center rounded-[4px] text-[8px] font-bold transition disabled:opacity-40',
                      subDone
                        ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                        : 'border-[1.5px] border-slate-300 bg-white text-transparent hover:border-emerald-500 hover:text-emerald-500',
                    ].join(' ')}
                  >
                    &#10003;
                  </button>
                ) : (
                  <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${STATUS_TONE[sub.status].dot}`} />
                )}
                <button
                  type="button"
                  onClick={() => onOpen(sub)}
                  title={`${formatTaskKey(sub.taskKey)} · ${TASK_STATUS_LABEL[sub.status]}`}
                  className="min-w-0 flex-1 truncate text-left text-[11px] leading-tight hover:underline"
                >
                  <span className={subClosed ? 'text-slate-400 line-through' : 'text-slate-700'}>
                    {sub.title}
                  </span>
                </button>
                {owner && (
                  <span
                    title={sub.assignees.map((a) => a.displayName).join(', ')}
                    className="grid h-4 w-4 shrink-0 place-items-center rounded-full bg-brand-100 text-[8px] font-bold text-brand-700"
                  >
                    {initials(owner.displayName)}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {footer}

      {/* Who has it, and the star - pinned to the bottom of every card. */}
      <div className="mt-auto flex items-center gap-1.5 pt-1">
        {!hideAssignee && (
          first ? (
            <span
              className="inline-flex min-w-0 items-center gap-1.5 text-[11px] font-semibold text-slate-600"
              title={task.assignees.map((a) => a.displayName).join(', ')}
            >
              <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-brand-100 text-[9px] font-bold text-brand-700">
                {initials(first.displayName)}
              </span>
              <span className="truncate">
                {first.displayName.split(/\s+/)[0]}
                {task.assignees.length > 1 && ` +${task.assignees.length - 1}`}
              </span>
            </span>
          ) : (
            <button
              type="button"
              onClick={() => onOpen(task)}
              className="rounded-full border border-dashed border-slate-300 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-400 transition hover:border-brand-400 hover:text-brand-600"
            >
              + Assign
            </button>
          )
        )}
        {onToggleImportant && (
          <span className="ml-auto">
            <StarButton
              active={task.isImportant}
              busy={busy}
              onToggle={() => onToggleImportant(task)}
            />
          </span>
        )}
      </div>
    </li>
  );
}

/** Section header + a grid of cards - the Kanban-style board look, minus
 *  columns and drag: each page keeps its own grouping (why these tasks are
 *  together), this just lays that group out as cards instead of rows. */
export function TaskSection({
  label, dot, count, hint, children,
}: { label: string; dot: string; count: number; hint?: string; children: ReactNode }) {
  return (
    <section>
      <div className="mb-2.5 flex items-center gap-2">
        <span className={`h-2 w-2 rounded-full ${dot}`} />
        <h2 className="text-[11px] font-bold uppercase tracking-[0.08em] text-slate-500">
          {label}
        </h2>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold tabular-nums text-slate-500">
          {count}
        </span>
        {hint && <span className="text-xs text-slate-400">&middot; {hint}</span>}
      </div>
      <ul className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] items-start gap-3">
        {children}
      </ul>
    </section>
  );
}

/** Loading / error / empty shells shared by every task list page. */
export function TaskListSkeleton() {
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-3">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="space-y-2 rounded-lg border border-slate-200 bg-white p-3">
          <div className="h-2 w-1/3 animate-pulse rounded bg-slate-100" />
          <div className="h-3 w-2/3 animate-pulse rounded bg-slate-100" />
          <div className="h-2.5 w-1/2 animate-pulse rounded bg-slate-100" />
        </div>
      ))}
    </div>
  );
}

export function TaskListError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-5 py-12 text-center">
      <p className="text-sm text-red-600">{message}</p>
      <button type="button" onClick={onRetry}
        className="mt-3 rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50">
        Try again
      </button>
    </div>
  );
}

export function TaskListEmpty({ title, hint }: { title: string; hint: string }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-white px-5 py-14 text-center">
      <p className="text-sm font-medium text-slate-900">{title}</p>
      <p className="mt-1 text-sm text-slate-500">{hint}</p>
    </div>
  );
}
